<template>
  <div v-if="isVisible" class="modal-overlay" @click.self="closeModal">
    <div class="modal-content">
      <div class="flip-card" :class="{ 'is-flipped': isFlipped }" @click="toggleFlip">
        <div class="flip-card-inner">
          <!-- Front Side - QR Code -->
          <div class="flip-card-front">
            <div class="card-header">
              <h3 class="card-header-title">Member QR Code</h3>
              <button type="button" class="close-button" @click.stop="closeModal">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>
            
            <div class="card-body">
              <div class="member-info">
                <div class="member-avatar-wrapper">
                  <img v-if="memberAvatar" :src="memberAvatar" alt="Member Avatar" class="member-avatar" />
                  <div v-else class="member-avatar-placeholder">{{ getInitials }}</div>
                </div>
                <h3 class="member-name">{{ displayMemberName }}</h3>
                <p class="member-club">{{ corporateName }} Member</p>
                <div class="member-tier">
                  <i class="fas fa-medal qr-medal-icon"></i>
                  <span>{{ displayTierName }}</span>
                </div>
              </div>

              <div class="qr-code-wrapper">
                <canvas id="canvas" class="qrcode"></canvas>
              </div>

              <!-- <p class="qr-instruction">Scan this QR code at any participating merchant</p>-->
            </div>
            <div class="card-footer">
              <p class="flip-hint">{{ isMobile ? 'Tap' : 'Hover' }} to see member details</p> 
            </div>
          </div>

          <!-- Back Side - Member Details -->
          <div class="flip-card-back">
            <div class="card-header">
              <h3 class="card-header-title">Member Details</h3>
              <button type="button" class="close-button" @click.stop="closeModal">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>
            
            <div class="card-body">
              <div class="member-stats" :class="{ 'single-grid': !isPointSystemActive }">
                <div class="stat-item">
                  <div class="stat-icon">
                    <img v-if="displayTierName !== 'Member'" :src="displayTierIcon" alt="Tier" class="tier-icon-img" />
                    <i v-else class="fas fa-medal fa-2x qr-medal-icon"></i>
                  </div>
                  <div class="stat-content">
                    <span class="stat-label">Tier</span>
                    <span class="stat-value">{{ displayTierName }}</span>
                  </div>
                </div>
                <div class="stat-item" v-if="isPointSystemActive">
                  <div class="stat-icon">
                    <img src="/img/member-icon/points-logo.svg" alt="Points" />
                  </div>
                  <div class="stat-content">
                    <span class="stat-label">Points</span>
                    <span class="stat-value">{{ displayPoints }}</span>
                  </div>
                </div>
                <div class="stat-item" v-if="isPointSystemActive">
                  <div class="stat-icon">
                    <img src="/img/member-icon/voucher-logo.svg" alt="Vouchers" />
                  </div>
                  <div class="stat-content">
                    <span class="stat-label">Vouchers</span>
                    <span class="stat-value">{{ displayVouchers }}</span>
                  </div>
                </div>
              </div>

              <div class="member-details">
                <div class="detail-item">
                  <span class="detail-label">Member ID</span>
                  <span class="detail-value">{{ displayMemberId }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Full Name</span>
                  <span class="detail-value">{{ displayMemberName }}</span>
                </div>
                <div class="detail-item" v-if="displayEmail">
                  <span class="detail-label">Email</span>
                  <span class="detail-value">{{ displayEmail }}</span>
                </div>
                <div class="detail-item" v-if="displayPhone">
                  <span class="detail-label">Phone</span>
                  <span class="detail-value">{{ displayPhone }}</span>
                </div>
              </div>

              <p class="flip-hint">{{ isMobile ? 'Tap' : 'Hover' }} to see QR code</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import QRCode from 'qrcode'
import { nextTick } from 'vue';
import { useStore } from 'vuex';
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { usePointSystem } from '@/composables/pointsystem';
import { useModalHistory } from '@/composables/useModalHistory';

export default {
  props: {
    isVisible: {
      type: Boolean,
      required: true
    },
    corporateLogo: {
      type: String,
      default: null
    },
    memberId: {
      type: String,
      required: true
    },
    memberName: {
      type: String,
      required: true
    },
    corporateName: {
      type: String,
      required: true
    },
    tierName: {
      type: String,
      default: ''
    },
    tierIcon: {
      type: String,
      default: ''
    },
    memberAvatar: {
      type: String,
      required: true
    },
    memberPoints: {
      type: [Number, String],
      default: 0
    },
    memberVouchers: {
      type: [Number, String],
      default: 0
    },
    memberEmail: {
      type: String,
      default: ''
    },
    memberPhone: {
      type: String,
      default: ''
    }
  },
  name: 'OQrCodeMemberModal',
  setup(props, { emit }) {
    const store = useStore();
    const { isPointSystemActive } = usePointSystem();
    const isFlipped = ref(false);
    const isMobile = ref(false);

    // Setup modal history untuk back button
    const { pushModalHistory, popModalHistory } = useModalHistory({
      isVisible: computed(() => props.isVisible),
      onClose: () => {
        emit('close');
        isFlipped.value = false;
      },
      stateKey: 'qr-code-modal'
    });

    // Ambil userInfo langsung dari store
    const userInfo = computed(() => store.state.Auth.userInfo);

    // Computed untuk data member - prioritas dari store userInfo
    const displayPoints = computed(() => {
      const points = userInfo.value?.member?.points ?? props.memberPoints ?? 0;
      return points;
    });

   
    const displayVouchers = computed(() => { // Prioritas: props dari parent (sudah ter-refresh setelah redeem/cancel) > store userInfo
      if (props.memberVouchers !== undefined && props.memberVouchers !== null) {
        return props.memberVouchers;
      }
      return userInfo.value?.member?.filtered_vouchers?.length ?? 0;
    });

    const displayEmail = computed(() => {
      const email = userInfo.value?.email || props.memberEmail || '';
      return email;
    });

    const displayPhone = computed(() => {
      const phone = userInfo.value?.user_profile?.phone || props.memberPhone || '';
      // Return '-' for null, empty, or default phone number
      if (!phone || phone === '+6281234567890' || phone === '6281234567890') {
        return '-';
      }
      return phone;
    });

    const displayTierName = computed(() => {
      const name = userInfo.value?.member?.tier_name || props.tierName;
      let finalName = name && name !== '-' ? name : 'Member';
      return finalName.length > 25 ? finalName.substring(0, 25) : finalName;
    });

    const displayMemberId = computed(() => {
      return userInfo.value?.member?.alias_id || props.memberId;
    });

    const displayMemberName = computed(() => {
      const u = userInfo.value;
      if (u && (u.first_name || u.last_name)) {
          return `${u.title || ''} ${u.first_name || ''} ${u.last_name || ''}`.trim();
      }
      return props.memberName;
    });

    const getInitials = computed(() => {
      const name = displayMemberName.value;
      if (!name) return '?';
      const words = name.split(' ').filter(Boolean);
      if (words.length >= 2) {
        return (words[0][0] + words[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    });

    const displayTierIcon = computed(() => {
      if (displayTierName.value === 'Member' || !displayTierName.value || displayTierName.value === '-') {
        return 'fas fa-medal';
      }
      // Prioritize prop (from Dashboard/API), then store, then default
      const propVal = props.tierIcon;
      const storeVal = userInfo.value?.member?.tier?.icon?.image_url;
      const storeVal2 = userInfo.value?.member?.tier?.image_url;
      
      
      const icon = propVal || storeVal || storeVal2 || '/img/member-icon/tier-logo.svg';
      
      return icon;
    });

    const checkMobile = () => {
      isMobile.value = window.innerWidth <= 768;
    };

    const closeModal = () => {
      emit('close');
      isFlipped.value = false;
      popModalHistory();
    };

    const toggleFlip = () => {
      if (isMobile.value) {
        isFlipped.value = !isFlipped.value;
      }
    };

    const loadQrCode = () => {
      var canvas = document.getElementById('canvas');
      const qrId = userInfo.value?.member?.id || props.memberId;
      const qrobj = { "memberId": qrId };
      const qrstring = JSON.stringify(qrobj);

      const options = {
        width: 220,
        margin: 2,
        scale: 10,
        color: {
          dark: '#1B1E28',
          light: '#FFFFFF'
        }
      };

      QRCode.toCanvas(canvas, qrstring, options, function (error) {
        if (error) console.error(error);
      });
    };

    onMounted(() => {
      checkMobile();
      window.addEventListener('resize', checkMobile);
    });

    onBeforeUnmount(() => {
      document.body.style.overflow = '';
      window.removeEventListener('resize', checkMobile);
    });

    // Watch isVisible
    watch(() => props.isVisible, (newVal) => {
      if (newVal) {
        document.body.style.overflow = 'hidden';
        nextTick(() => {
          loadQrCode();
        });
        pushModalHistory();
      } else {
        document.body.style.overflow = '';
        isFlipped.value = false;
      }
    });

    return {
      isFlipped,
      isMobile,
      getInitials,
      displayMemberName,
      displayMemberId,
      displayPoints,
      displayVouchers,
      displayEmail,
      displayPhone,
      displayTierName,
      displayTierIcon,
      isPointSystemActive,
      closeModal,
      toggleFlip,
      loadQrCode
    };
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.modal-content {
  max-width: 420px;
  width: 90%;
  position: relative;
  animation: slideUp 0.3s ease;
  background: transparent;
  border: none;
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.close-button {
  position: absolute;
  top: 50%;
  right: 16px;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #FFFFFF;
  transition: all 0.2s ease;
  z-index: 20;
  opacity: 1;
}

.close-button:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-50%) rotate(90deg);
}

/* Hide front close button when flipped */
.flip-card.is-flipped .flip-card-front .close-button {
  opacity: 0;
  pointer-events: none;
}

/* Hide back close button when not flipped */
.flip-card:not(.is-flipped) .flip-card-back .close-button {
  opacity: 0;
  pointer-events: none;
}

/* On desktop hover, show appropriate close button */
@media (min-width: 769px) {
  .flip-card:hover .flip-card-front .close-button {
    opacity: 0;
    pointer-events: none;
  }
  
  .flip-card:hover .flip-card-back .close-button {
    opacity: 1;
    pointer-events: auto;
  }
  
  .flip-card.is-flipped:hover .flip-card-front .close-button {
    opacity: 1;
    pointer-events: auto;
  }
  
  .flip-card.is-flipped:hover .flip-card-back .close-button {
    opacity: 0;
    pointer-events: none;
  }
}

.flip-card {
  -webkit-perspective: 1000px;
  perspective: 1000px;
  width: 100%;
  height: 600px;
  cursor: pointer;
}

.flip-card-inner {
  position: relative;
  width: 100%;
  height: 600px;
  transition: -webkit-transform 0.6s;
  transition: transform 0.6s;
  -webkit-transform-style: preserve-3d;
  transform-style: preserve-3d;
  /* Fix for iOS Safari rendering the backface before the 3D transform settles */
  -webkit-transform: translateZ(0) rotateY(0deg);
  transform: translateZ(0) rotateY(0deg);
}

.flip-card.is-flipped .flip-card-inner {
  -webkit-transform: translateZ(0) rotateY(180deg);
  transform: translateZ(0) rotateY(180deg);
}

@media (min-width: 769px) {
  .flip-card {
    cursor: default;
  }
  
  .flip-card:hover .flip-card-inner {
    -webkit-transform: translateZ(0) rotateY(180deg);
    transform: translateZ(0) rotateY(180deg);
  }
  
  .flip-card.is-flipped:hover .flip-card-inner {
    -webkit-transform: translateZ(0) rotateY(0deg);
    transform: translateZ(0) rotateY(0deg);
  }
}

.flip-card-front,
.flip-card-back {
  position: absolute;
  width: 100%;
  height: 600px;
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
}

.flip-card-front {
  background: #FFFFFF;
  -webkit-transform: rotateY(0deg);
  transform: rotateY(0deg);
}

.flip-card-back {
  background: #FFFFFF;
  -webkit-transform: rotateY(180deg);
  transform: rotateY(180deg);
}

.card-header {
  background: var(--primary-gradient, linear-gradient(135deg, var(--primary-color) 0%, var(--primary-color) 100%));
  padding: 20px 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  flex-shrink: 0;
}

.card-header-title {
  font-size: 18px;
  font-weight: 600;
  color: #FFFFFF;
  margin: 0;
}

.card-body {
  padding: 32px 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
  flex: 1;
  overflow-y: auto;
}

/* Specific to Front Side - No Scrollbar */
.flip-card-front .card-body {
  overflow-y: hidden;
  padding: 16px 24px;
  justify-content: center;
  gap: 16px;
}

/* Adjust gaps for front side content */
.flip-card-front .member-info {
  gap: 8px;
}

.card-footer {
  padding: 0 24px 24px;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
}

.member-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.member-avatar-wrapper {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  overflow: hidden;
  border: 4px solid var(--primary-border, var(--primary-color, #F93E3E));
  box-shadow: 0 8px 24px var(--secondary-color, rgba(249, 62, 62, 0.2));
}

.member-avatar {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.member-avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--primary-gradient, linear-gradient(135deg, var(--primary-color, #F93E3E) 0%, var(--primary-color, #E02020) 100%));
  color: #FFFFFF;
  font-size: 28px;
  font-weight: 600;
}

.member-name {
  font-size: 20px;
  font-weight: 600;
  color: #1B1E28;
  margin: 0;
  text-align: center;
  line-height: 1.3;
  word-break: break-word;
  overflow-wrap: anywhere;
  max-width: 100%;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.member-club {
  font-size: 13px;
  color: #717784;
  margin: 0;
  text-align: center;
}

.member-tier {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: var(--secondary-color, rgba(249, 62, 62, 0.08));
  border-radius: 20px;
  color: var(--primary-color, #F93E3E);
  font-size: 13px;
  font-weight: 600;
}

.member-tier i {
  font-size: 14px;
}

.qr-code-wrapper {
  background: #FFFFFF;
  padding: 20px;
  border-radius: 16px;
  border: 2px solid #E1E4EA;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
}

.qrcode {
  display: block;
  border-radius: 8px;
}

.qr-instruction {
  font-size: 13px;
  color: #717784;
  text-align: center;
  margin: 0;
  line-height: 1.5;
}

.flip-hint {
  font-size: 12px;
  color: var(--primary-color, #F93E3E);
  text-align: center;
  margin: 0;
  font-weight: 500;
}

.qr-medal-icon {
  color: var(--primary-icon, #F93E3E);
}

.member-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  width: 100%;
  margin-bottom: 8px;
}

.member-stats.single-grid {
  grid-template-columns: 1fr;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px 12px;
  background: #F5F6FA;
  border-radius: 12px;
}

.stat-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-icon img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.stat-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.stat-label {
  font-size: 11px;
  color: #717784;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.stat-value {
  font-size: 16px;
  font-weight: 600;
  color: #1B1E28;
}

.member-details {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-bottom: 16px;
  border-bottom: 1px solid #E1E4EA;
}

.detail-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.detail-label {
  font-size: 12px;
  color: #717784;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.detail-value {
  font-size: 15px;
  font-weight: 500;
  color: #1B1E28;
  word-break: break-word;
}

@media (max-width: 768px) {
  .modal-content {
    width: 95%;
  }

  .close-button {
    width: 28px;
    height: 28px;
    right: 12px;
  }

  .flip-card,
  .flip-card-inner,
  .flip-card-front,
  .flip-card-back {
    height: 580px;
  }

  .card-body {
    padding: 20px 16px;
    gap: 16px;
  }

  .flip-card-front .card-body {
    padding: 12px 16px;
    gap: 10px;
    justify-content: space-evenly;
  }

  .flip-card-front .member-info {
    gap: 6px;
  }

  .member-avatar-wrapper {
    width: 64px;
    height: 64px;
    border-width: 3px;
  }

  .member-avatar-placeholder {
    font-size: 22px;
  }

  .member-name {
    font-size: 16px;
    line-height: 1.25;
  }

  .qr-code-wrapper {
    padding: 12px;
  }

  .card-footer {
    padding: 0 16px 12px;
  }

  .member-stats {
    gap: 8px;
  }

  .stat-item {
    padding: 12px 8px;
  }

  .stat-icon {
    width: 28px;
    height: 28px;
  }

  .stat-value {
    font-size: 14px;
  }

  .detail-value {
    font-size: 14px;
  }
}
</style>